/**
 * Token Refresh Utility
 * Handles automatic JWT token refresh before expiration
 */

import axios from "axios";
import { config } from "../config/config";
import { getDeviceId } from "./deviceId";
import { getVisitorToken } from "./visitorToken";

// Timer reference for cleanup
let refreshTimer = null;
let refreshPromise = null;

// Clear only auth/session keys. Do not wipe feature-level keys like
// deal_local_draft so in-progress create flows survive token refresh failures.
const clearAuthStorageOnly = () => {
    try {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        localStorage.removeItem("merchantId");
        localStorage.removeItem("influencerId");
        localStorage.removeItem("profileComplete");
    } catch {
        // no-op
    }
};

/**
 * Decode JWT token to get payload (without verification)
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded payload or null
 */
export const decodeToken = (token) => {
    try {
        if (!token) return null;
        const base64Payload = token.split('.')[1];
        // Use atob for browser-compatible base64 decoding
        const payload = atob(base64Payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(payload);
    } catch (error) {
        console.error("Error decoding token:", error);
        return null;
    }
};

/**
 * Get token expiration time in milliseconds
 * @param {string} token - JWT token
 * @returns {number|null} Expiration timestamp in ms or null
 */
export const getTokenExpiration = (token) => {
    const decoded = decodeToken(token);
    if (decoded?.exp) {
        return decoded.exp * 1000; // Convert seconds to milliseconds
    }
    return null;
};

/**
 * Check if token is expired or about to expire (within buffer time)
 * @param {string} token - JWT token
 * @param {number} bufferMs - Buffer time in ms before expiration (default 30 seconds)
 * @returns {boolean} True if token is expired or about to expire
 */
export const isTokenExpired = (token, bufferMs = 30000) => {
    const expiration = getTokenExpiration(token);
    if (!expiration) return true;
    return Date.now() >= (expiration - bufferMs);
};

/**
 * Refresh the access token using refresh token
 * @returns {Promise<Object|null>} New tokens or null on failure
 */
export const refreshAccessToken = async () => {
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = (async () => {
        try {
            const refreshToken = localStorage.getItem("refreshToken");
            const accessToken = localStorage.getItem("token");
            // console.log("63---->REFRESH TOKEN PAYLOAD---->", { refreshToken });
            // console.log("64---->ACCESS TOKEN PAYLOAD---->", { accessToken });


            // Guard against corrupted "undefined" string stored by older code
            if (!refreshToken || refreshToken === "undefined" || refreshToken === "null") {
                // // console.log("No valid refresh token available");
                localStorage.removeItem("refreshToken");
                return null;
            }

            // console.log("Refreshing access token...");

            const deviceId = getDeviceId();
            const visitorToken = getVisitorToken();
            const refreshPayload = {
                refreshToken,
                ...(deviceId ? { deviceId } : {}),
                ...(visitorToken ? { visitorToken } : {}),
            };

            const refreshHeaders = {
                'Content-Type': 'application/json',
                ...(deviceId ? { 'x-device-id': deviceId } : {}),
                ...(visitorToken ? { 'x-visitor-token': visitorToken } : {}),
            };

            const response = await axios.post(
                `${config.localApiUrl}/api/auth/refresh-token`,
                refreshPayload,
                {
                    headers: refreshHeaders,
                    timeout: 10000,
                }
            );

            const data = response.data;

            if (data?.success && data?.accessToken) {
                // Store new tokens
                localStorage.setItem("token", data.accessToken);
                if (data.refreshToken) {
                    localStorage.setItem("refreshToken", data.refreshToken);
                }

                // console.log("Token refreshed successfully");

                // Schedule next refresh
                scheduleTokenRefresh(data.accessToken);

                return {
                    accessToken: data.accessToken,
                    refreshToken: data.refreshToken || refreshToken
                };
            } else {
                console.error("Failed to refresh token:", data?.message);
                return null;
            }
        } catch (error) {
            console.error("Error refreshing token:", error);
            return null;
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
};

/**
 * Schedule automatic token refresh before expiration
 * @param {string} token - Current access token
 */
export const scheduleTokenRefresh = (token) => {
    // Clear any existing timer
    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }

    if (!token) return;

    const expiration = getTokenExpiration(token);
    if (!expiration) return;

    // Calculate time until refresh (refresh 1 minute before expiration)
    const now = Date.now();
    const refreshBuffer = 60000; // 1 minute before expiration
    const timeUntilRefresh = expiration - now - refreshBuffer;

    if (timeUntilRefresh <= 0) {
        // Token is already expired or about to expire, refresh immediately
        // console.log("Token expired or about to expire, refreshing now...");
        refreshAccessToken();
        return;
    }

    // console.log(`Token refresh scheduled in ${Math.round(timeUntilRefresh / 1000)} seconds`);

    refreshTimer = setTimeout(async () => {
        const result = await refreshAccessToken();
        if (!result) {
            // Refresh failed, clear auth and redirect to login
            // console.log("Token refresh failed, logging out...");
            const { logout } = await import("./auth");
            await logout(true, "/login", true);
        }
    }, timeUntilRefresh);
};

/**
 * Initialize token refresh on app start
 * Should be called when app loads if user is authenticated
 */
export const initializeTokenRefresh = () => {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");

    if (token && refreshToken) {
        // Check if token is already expired
        if (isTokenExpired(token, 0)) {
            // Token expired, try to refresh immediately
            // console.log("Access token expired, attempting refresh...");
            refreshAccessToken().then(async (result) => {
                if (!result) {
                    // console.log("Could not refresh expired token, redirecting to login...");
                    const { logout } = await import("./auth");
                    await logout(true, "/login", true);
                }
            });
        } else {
            // Token still valid, schedule refresh
            scheduleTokenRefresh(token);
        }
    }
};

/**
 * Clear token refresh timer (call on logout)
 */
export const clearTokenRefresh = () => {
    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }
};

/**
 * Store tokens after login/signup
 * @param {string} accessToken - Access token (or legacy token)
 * @param {string} refreshToken - Refresh token (optional for backward compatibility)
 */
export const storeTokens = (accessToken, refreshToken) => {
    if (accessToken) {
        localStorage.setItem("token", accessToken);
    }
    if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
    }

    // Schedule refresh if we have both tokens
    if (accessToken && refreshToken) {
        scheduleTokenRefresh(accessToken);
    }
};
